import React, { useState, useEffect, useCallback } from "react";
import { api, formatDKK } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Download, Loader2, Trash2, ExternalLink } from "lucide-react";
import { KitchenView, OtherView, TechDetails, SERVICE_RU, TIME_RU, SOURCE_RU } from "./LeadConfigView";

const STATUSES = ["New", "Needs Review", "Need More Information", "Quote Sent", "Price Ready", "Customer Accepted", "Booked", "Assembler Needed", "Assembler Assigned", "Scheduled", "Completed", "Cancelled"];
const SERVICES = ["pax", "other", "kitchen"];

const statusColor = (s) => ({
  "New": "bg-blue-100 text-blue-800", "Needs Review": "bg-amber-100 text-amber-800",
  "Need More Information": "bg-rose-100 text-rose-800",
  "Quote Sent": "bg-indigo-100 text-indigo-800", "Price Ready": "bg-cyan-100 text-cyan-800",
  "Customer Accepted": "bg-emerald-100 text-emerald-800", "Booked": "bg-violet-100 text-violet-800",
  "Assembler Needed": "bg-orange-100 text-orange-800", "Assembler Assigned": "bg-teal-100 text-teal-800",
  "Scheduled": "bg-purple-100 text-purple-800", "Completed": "bg-green-100 text-green-800",
  "Cancelled": "bg-stone-200 text-stone-600",
}[s] || "bg-stone-100 text-stone-700");

export default function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", service_type: "", postcode: "", date: "" });
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => v && (params[k] = v));
      const res = await api.get("/admin/leads", { params });
      setLeads(res.data);
    } catch (e) { toast.error("Не удалось загрузить заявки"); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const remove = async (l) => {
    if (!window.confirm(`Удалить заявку от ${l.first_name} ${l.last_name}? Это действие необратимо.`)) return;
    try {
      await api.delete(`/admin/leads/${l.id}`);
      toast.success("Заявка удалена");
      if (selected?.id === l.id) setSelected(null);
      load();
    } catch (e) { toast.error("Не удалось удалить заявку"); }
  };

  const inputCls = "px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-[#1E3A2F]";

  return (
    <div data-testid="admin-leads-tab">
      <div className="flex flex-wrap gap-2 mb-4">
        <select className={inputCls} value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="filter-status">
          <option value="">Все статусы</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={inputCls} value={filters.service_type} onChange={(e) => setFilters((f) => ({ ...f, service_type: e.target.value }))} data-testid="filter-service">
          <option value="">Все услуги</option>
          {SERVICES.map((s) => <option key={s} value={s}>{SERVICE_RU[s]}</option>)}
        </select>
        <input className={inputCls} placeholder="Индекс" value={filters.postcode} onChange={(e) => setFilters((f) => ({ ...f, postcode: e.target.value }))} data-testid="filter-postcode" />
        <input type="date" className={inputCls} value={filters.date} onChange={(e) => setFilters((f) => ({ ...f, date: e.target.value }))} data-testid="filter-date" />
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Клиент</th>
                <th className="text-left px-4 py-3">Услуга</th>
                <th className="text-left px-4 py-3">Индекс</th>
                <th className="text-left px-4 py-3">Цена (с НДС)</th>
                <th className="text-left px-4 py-3">Статус</th>
                <th className="text-left px-4 py-3">Дата</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-stone-400">Загрузка…</td></tr>}
              {!loading && leads.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-stone-400">Заявок пока нет</td></tr>}
              {leads.map((l) => (
                <tr key={l.id} onClick={() => setSelected(l)} className="hover:bg-stone-50 cursor-pointer" data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3 font-medium text-stone-900">{l.first_name} {l.last_name}<div className="text-xs text-stone-400">{l.email}</div></td>
                  <td className="px-4 py-3 text-xs font-semibold text-stone-600">{SERVICE_RU[l.service_type] || l.service_type}</td>
                  <td className="px-4 py-3">{l.postcode}</td>
                  <td className="px-4 py-3 font-mono">{l.customer_price_incl_vat ? formatDKK(l.customer_price_override || l.customer_price_incl_vat) : "—"}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusColor(l.status)}`}>{l.status}</span></td>
                  <td className="px-4 py-3 text-stone-500 text-xs">{(l.created_at || "").split("T")[0]}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={(e) => { e.stopPropagation(); remove(l); }} className="text-rose-600 hover:text-rose-800" title="Удалить заявку" data-testid={`delete-lead-${l.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <LeadDetail lead={selected} onClose={() => setSelected(null)} onSaved={(u) => { setSelected(u); load(); }} onDeleted={() => { setSelected(null); load(); }} />}
    </div>
  );
}

function LeadDetail({ lead, onClose, onSaved, onDeleted }) {
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get(`/admin/leads/${lead.id}`).then((r) => {
      setDetail(r.data);
      setForm({
        status: r.data.status,
        internal_notes: r.data.internal_notes || "",
        customer_price_override: r.data.customer_price_override ?? "",
        actual_fulfillment_cost: r.data.actual_fulfillment_cost ?? "",
        subcontractor_vat_deductible: !!r.data.subcontractor_vat_deductible,
        other_direct_costs: r.data.other_direct_costs ?? "",
      });
    });
  }, [lead.id]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        status: form.status,
        internal_notes: form.internal_notes,
        customer_price_override: form.customer_price_override === "" ? null : parseFloat(form.customer_price_override),
        actual_fulfillment_cost: form.actual_fulfillment_cost === "" ? null : parseFloat(form.actual_fulfillment_cost),
        subcontractor_vat_deductible: !!form.subcontractor_vat_deductible,
        other_direct_costs: form.other_direct_costs === "" ? null : parseFloat(form.other_direct_costs),
      };
      const res = await api.patch(`/admin/leads/${lead.id}`, payload);
      toast.success("Сохранено");
      onSaved(res.data);
    } catch (e) { toast.error("Ошибка сохранения"); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm(`Удалить заявку от ${lead.first_name} ${lead.last_name}? Это действие необратимо.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/leads/${lead.id}`);
      toast.success("Заявка удалена");
      onDeleted();
    } catch (e) { toast.error("Не удалось удалить заявку"); }
    finally { setDeleting(false); }
  };

  const download = async (fileId, name, openOnly = false) => {
    const win = openOnly ? window.open("", "_blank") : null;
    try {
      const res = await api.get(`/admin/leads/${lead.id}/files/${fileId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (win) { win.location.href = url; } else { const a = document.createElement("a"); a.href = url; a.download = name; a.click(); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (win) win.close(); toast.error("Ошибка загрузки файла"); }
  };

  const inputCls = "w-full px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-[#1E3A2F]";
  const internal = detail?.internal_snapshot;
  const actual = detail?.actual_profit;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="lead-detail-dialog">
        <DialogHeader><DialogTitle className="font-display break-words">{lead.first_name} {lead.last_name} · {SERVICE_RU[lead.service_type] || lead.service_type}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Детали заявки</DialogDescription>
        {!detail ? <div className="py-8 text-center text-stone-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
          <div className="space-y-5 text-sm">
            {/* Contact */}
            <section className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 break-words">
              <Info label="E-mail" value={detail.email} />
              <Info label="Телефон" value={detail.phone} />
              <Info label="Индекс" value={detail.postcode} />
              <Info label="Адрес" value={detail.address || "—"} />
              <Info label="Желаемая дата" value={detail.preferred_date || "—"} />
              <Info label="Интервал времени" value={TIME_RU[detail.preferred_time] || detail.preferred_time || "—"} />
            </section>
            {detail.comments && <div className="bg-stone-50 rounded-lg p-3"><span className="text-xs text-stone-400 uppercase">Комментарии</span><p className="text-stone-700 whitespace-pre-wrap break-words">{detail.comments}</p></div>}

            {detail.manual_price_review && (
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="lead-review-flag">
                <span className="text-amber-800 text-sm font-semibold">⚠ Требуется ручная проверка цены</span>
                <span className="text-xs text-amber-700">{(detail.review_reasons || []).join(", ")}</span>
              </div>
            )}
            <div className="text-xs text-stone-500">Способ заполнения: <b className="text-stone-800">{SOURCE_RU[detail.source || "manual"] || detail.source}</b></div>
            {detail.service_type !== "pax" && <div className="text-xs text-stone-500" data-testid="lead-ad-source">Рекламный источник: <b className="text-stone-800">{classifySource(detail.attribution || {})}</b></div>}

            {/* Client source / lead attribution */}
            {detail.service_type === "pax" && <section className="bg-stone-50 rounded-lg p-3" data-testid="lead-attribution">
              <div className="text-xs text-stone-400 uppercase mb-2">Источник клиента</div>
              {(() => {
                const a = detail.attribution || {};
                const sm = [a.utm_source, a.utm_medium].filter(Boolean).join(" / ") || "—";
                return (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    <Info label="Классификация" value={classifySource(a)} />
                    <Info label="Source / Medium" value={sm} />
                    <Info label="Кампания" value={a.utm_campaign || "—"} />
                    {a.gclid && <Info label="GCLID" value={a.gclid} />}
                    <Info label="Первая страница" value={a.initial_landing_page || "—"} />
                    <Info label="Реферер" value={a.initial_referrer || "—"} />
                  </div>
                );
              })()}
            </section>}

            {detail.recognized_components?.length > 0 && (
              <section className="bg-stone-50 rounded-lg p-3" data-testid="lead-recognized">
                <div className="text-xs text-stone-400 uppercase mb-1">Распознанные компоненты (AI)</div>
                <ul className="text-xs text-stone-700 space-y-0.5 max-h-40 overflow-y-auto">
                  {detail.recognized_components.map((it, i) => (
                    <li key={i}>{it.quantity}× {it.name || it.category} {it.article ? `(${it.article})` : ""} — {Math.round((it.confidence || 0) * 100)}%</li>
                  ))}
                </ul>
              </section>
            )}

            {/* Configuration */}
            {detail.service_type === "kitchen" ? <KitchenView config={detail.configuration || {}} />
              : detail.service_type === "other" ? <OtherView config={detail.configuration || {}} />
              : (
            <section className="bg-stone-50 rounded-lg p-3">
              <div className="text-xs text-stone-400 uppercase mb-1">Конфигурация</div>
              <pre className="text-xs text-stone-700 whitespace-pre-wrap break-words font-mono">{JSON.stringify(detail.configuration, null, 2)}</pre>
            </section>)}

            {/* Files */}
            {detail.files?.length > 0 && (
              <section>
                <div className="text-xs text-stone-400 uppercase mb-2">Загруженные файлы</div>
                <ul className="space-y-2">
                  {detail.files.map((f) => (
                    <li key={f.id} className="flex flex-wrap items-center gap-2 bg-white border border-stone-200 rounded-lg px-3 py-2" data-testid={`lead-file-${f.id}`}>
                      <FileText className="w-4 h-4 text-stone-400 shrink-0" />
                      <span className="flex-1 min-w-0 break-all">{f.original_filename}</span>
                      <button onClick={() => download(f.id, f.original_filename, true)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#1E3A2F] text-[#1E3A2F] text-xs font-semibold hover:bg-[#1E3A2F]/5" data-testid={`open-file-${f.id}`}><ExternalLink className="w-3.5 h-3.5" /> Открыть</button>
                      <button onClick={() => download(f.id, f.original_filename)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#1E3A2F] text-white text-xs font-semibold hover:bg-[#162D24]" data-testid={`download-file-${f.id}`}><Download className="w-3.5 h-3.5" /> Скачать</button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {detail.service_type !== "pax" && <TechDetails detail={detail} />}

            {/* Internal pricing (ADMIN ONLY) */}
            {internal && (
              <section className="bg-[#1E3A2F]/5 border border-[#1E3A2F]/15 rounded-lg p-3" data-testid="lead-internal-pricing">
                <div className="text-xs text-[#1E3A2F] uppercase font-semibold mb-2">Внутренние расчёты (только админ)</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <Info label="Ориент. себестоимость" value={formatDKK(internal.fulfillment_cost)} />
                  <Info label="Комиссия платформы" value={formatDKK(internal.platform_fee)} />
                  <Info label="Ориент. валовая прибыль" value={formatDKK(internal.estimated_gross_profit)} />
                  <Info label="Ориент. валовая маржа" value={`${Math.round((internal.estimated_gross_margin || 0) * 100)}%`} />
                </div>
                {internal.below_min_profit && <p className="text-xs text-red-600 mt-2">⚠ Ниже минимальной валовой прибыли ({formatDKK(internal.min_gross_profit_per_job)})</p>}
              </section>
            )}

            {/* Editable admin fields */}
            <section className="space-y-3 border-t border-stone-200 pt-4">
              <div>
                <label className="block text-xs text-stone-500 uppercase mb-1">Статус</label>
                <select className={inputCls} value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} data-testid="lead-status-select">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <NumField label="Цена клиента (с НДС)" v={form.customer_price_override} on={(v) => setForm((f) => ({ ...f, customer_price_override: v }))} testId="lead-price-override" />
                <NumField label="Субподрядчик: цена брутто (DKK)" v={form.actual_fulfillment_cost} on={(v) => setForm((f) => ({ ...f, actual_fulfillment_cost: v }))} testId="lead-actual-cost" />
                <NumField label="Доп. расходы" v={form.other_direct_costs} on={(v) => setForm((f) => ({ ...f, other_direct_costs: v }))} testId="lead-other-costs" />
              </div>
              <label className="flex items-start gap-2 text-sm text-stone-700" data-testid="lead-vat-deductible-label">
                <input type="checkbox" className="mt-0.5 w-4 h-4 accent-[#1E3A2F]" checked={!!form.subcontractor_vat_deductible} onChange={(e) => setForm((f) => ({ ...f, subcontractor_vat_deductible: e.target.checked }))} data-testid="lead-vat-deductible" />
                <span>Momsregistreret + valid VAT invoice (НДС вычитаемый)</span>
              </label>
              {actual && actual.actual_gross_profit != null && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 grid grid-cols-2 gap-x-4 gap-y-1" data-testid="lead-actual-profit">
                  <Info label="Выручка без НДС" value={formatDKK(actual.revenue_excl_vat)} />
                  <Info label="НДС (с клиента)" value={formatDKK(actual.vat_amount)} />
                  <Info label="Субподряд брутто" value={formatDKK(actual.subcontractor_gross)} />
                  <Info label="Вычитаемый НДС (input VAT)" value={formatDKK(actual.subcontractor_input_vat)} />
                  <Info label="Себестоимость субподряда без НДС" value={formatDKK(actual.subcontractor_effective_cost)} />
                  <Info label="Ожидаемая прибыль" value={formatDKK(actual.actual_gross_profit)} />
                  <Info label="Маржа прибыли" value={`${Math.round((actual.actual_gross_margin || 0) * 100)}%`} />
                </div>
              )}
              <div>
                <label className="block text-xs text-stone-500 uppercase mb-1">Внутренние заметки</label>
                <textarea rows={3} className={inputCls} value={form.internal_notes} onChange={(e) => setForm((f) => ({ ...f, internal_notes: e.target.value }))} data-testid="lead-notes" />
              </div>
              <button onClick={save} disabled={saving} className="w-full py-3 rounded-full bg-[#1E3A2F] text-white font-semibold disabled:opacity-60" data-testid="lead-save-btn">
                {saving ? "Сохранение…" : "Сохранить изменения"}
              </button>
              <button onClick={del} disabled={deleting} className="w-full py-2.5 rounded-full border border-rose-300 text-rose-700 font-semibold hover:bg-rose-50 disabled:opacity-60 flex items-center justify-center gap-2" data-testid="lead-delete-btn">
                <Trash2 className="w-4 h-4" /> {deleting ? "Удаление…" : "Удалить заявку"}
              </button>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }) {
  return <div><span className="text-xs text-stone-400">{label}: </span><span className="font-medium text-stone-800">{value}</span></div>;
}

function classifySource(a = {}) {
  const src = (a.utm_source || "").toLowerCase();
  const med = (a.utm_medium || "").toLowerCase();
  const ref = (a.initial_referrer || "").toLowerCase();
  if (a.gclid || (src === "google" && med === "cpc") || med === "cpc" || med === "ppc" || med === "paid") return "Google Ads";
  if (src.includes("facebook") || src.includes("instagram") || src === "fb" || src === "ig" ||
      ref.includes("facebook.") || ref.includes("instagram.") || ref.includes("fb.")) return "Facebook";
  if (src.includes("google") || ref.includes("google.")) return "Google Organic";
  if (!src && !med && !a.gclid && !ref) return "Direct";
  return "Other";
}
function NumField({ label, v, on, testId }) {
  return (
    <div>
      <label className="block text-xs text-stone-500 mb-1">{label}</label>
      <input type="number" step="0.01" className="w-full px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-[#1E3A2F]" value={v} onChange={(e) => on(e.target.value)} data-testid={testId} />
    </div>
  );
}
